import React from "react";
import { CheckCircle2, FileText, Wheat, Clock, XCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { HEADER, INFRA_ITEMS, SOCIAL_ITEMS } from "@/mock/data";
import { fileLabel } from "@/components/steps/FormSteps4";
import ApplicationTracker from "@/components/ApplicationTracker";

const STATUS_MAP = {
  SUBMITTED: { label: "सादर केला", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: Clock },
  APPROVED: { label: "मंजूर", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  REJECTED: { label: "नामंजूर", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  DRAFT: { label: "प्रारूप", cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Clock },
};

function fmt(v) {
  if (v === undefined || v === null || v === "" || v === "-- निवडा --") return "—";
  if (typeof v === "object") return v.name || "—";
  if (v === "yes") return "आहे";
  if (v === "no") return "नाही";
  return String(v);
}

function DL({ rows }) {
  return (
    <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
      {rows.map(([l, v], i) => (
        <div key={i} className="flex gap-2 border-b border-dotted border-slate-200 py-1.5 text-[13px]">
          <span className="min-w-[150px] text-slate-500">{l}:</span>
          <span className="font-semibold text-slate-800">{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function SuccessScreen() {
  const { application } = useApp();

  if (!application) {
    return <div className="flex min-h-[60vh] items-center justify-center text-slate-500">लोड होत आहे...</div>;
  }

  const d = application.data || {};
  const st = STATUS_MAP[application.status] || STATUS_MAP.SUBMITTED;
  const dt = application.submittedAt ? new Date(application.submittedAt) : new Date();
  const stamp = dt.toLocaleString("en-GB", { hour12: false }).replace(",", "");

  return (
    <div className="min-h-[80vh] bg-[#f6f2e6] px-4 py-8">
      {/* On-screen success banner (hidden in print) */}
      <div className="no-print mx-auto mb-6 max-w-3xl rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-100 animate-fade-up">
        <div className={`mx-auto mb-4 flex h-[70px] w-[70px] items-center justify-center rounded-full animate-pop ${application.status === "REJECTED" ? "bg-red-100" : "bg-emerald-100"}`}>
          <st.Icon className={`h-10 w-10 ${application.status === "REJECTED" ? "text-red-500" : "text-[#5a9e3a]"}`} />
        </div>
        <p className="text-[14px] font-bold text-[#3a6b1c]">अर्ज {st.label} आहे</p>
        <h2 className="mt-1 text-[24px] font-bold text-[#1b5e3f]">
          {application.status === "APPROVED"
            ? "तुमचा अर्ज मंजूर झाला आहे."
            : application.status === "REJECTED"
            ? "तुमचा अर्ज नामंजूर करण्यात आला आहे."
            : "तुमचा अर्ज यशस्वीरित्या सादर झाला आहे."}
        </h2>
        <p className="mt-2 text-[15px] text-slate-500">तुमचा अर्ज जिल्हा सेतू समिती कार्यालयाकडे पाठवण्यात आला आहे.</p>

        {application.adminRemark && (
          <div className="mx-auto mt-4 max-w-md rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-left text-[14px]">
            <span className="text-slate-500">कार्यालय शेरा:</span> <strong>{application.adminRemark}</strong>
          </div>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button onClick={() => window.print()} className="flex items-center gap-2 rounded-full bg-[#1b5e3f] px-6 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90" data-testid="download-pdf-btn">
            <FileText className="h-4 w-4" /> PDF डाउनलोड करा
          </button>
        </div>
      </div>

      {/* अर्जाचा मागोवा — live status tracker */}
      <div className="no-print mx-auto mb-6 max-w-3xl animate-fade-up">
        <ApplicationTracker application={application} />
      </div>

      {/* Printable official receipt */}
      <div id="assk-receipt" className="mx-auto max-w-3xl rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-100">
        <div className="flex items-center gap-4 border-b-2 border-[#1b5e3f] pb-4">
          <div className="flex h-16 w-16 min-w-[4rem] items-center justify-center rounded-full border-[3px] border-[#E0A106] bg-white">
            <Wheat className="h-8 w-8 text-[#1b5e3f]" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[13px] font-medium text-slate-600">{HEADER.govt}</p>
            <h1 className="text-[18px] font-bold text-[#1b5e3f]">{HEADER.title}</h1>
            <p className="text-[12px] text-slate-500">{HEADER.sub}</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
          <div>
            <p className="text-[12px] text-slate-500">अर्ज क्रमांक</p>
            <p className="text-[20px] font-bold text-[#1b5e3f] font-latin">{application.applicationNumber}</p>
          </div>
          <div>
            <p className="text-[12px] text-slate-500">सादर केल्याची तारीख व वेळ</p>
            <p className="text-[15px] font-bold text-slate-800 font-latin">{stamp}</p>
          </div>
          <span className={`rounded-full border px-4 py-1.5 text-[13px] font-semibold ${st.cls}`}>स्थिती: {st.label}</span>
        </div>

        <Section title="१. वैयक्तिक माहिती">
          <DL rows={[
            ["अर्ज करावयाच्या स्थानिक स्वराज्य संस्थेचा प्रकार", d.orgType], ["तालुका (संस्था)", d.taluka], ["स्थानिक स्वराज्य संस्था (ग्रामपंचायत / गाव)", d.village],
            ["नाव (मराठी)", d.nameMr], ["नाव (इंग्रजी)", d.nameEn],
            ["जन्म तारीख", d.dob], ["वय", d.age], ["लिंग", d.gender],
            ["मोबाईल", d.mobile], ["ई-मेल", d.email],
            ["आधार क्रमांक", d.aadhaar], ["आधारकार्ड अपलोड", d.aadhaar_file],
            ["पॅन क्रमांक", d.pan], ["पॅनकार्ड अपलोड", d.pan_file],
            ["नाव बदल गॅझेट / विवाह दाखला", d.name_change_file],
            ["रहिवासाचा पत्ता", d.address], ["गाव/शहर", d.city], ["वॉर्ड क्रमांक", d.wardNo],
            ["तालुका", d.talukaRO || d.taluka], ["जिल्हा", d.districtRO], ["पिनकोड", d.pincode],
            ["रहिवासी पत्त्याचा पुरावा १", d.residence_proof_file], ["रहिवासी पत्त्याचा पुरावा २", d.residence_proof_file_2], ["स्वघोषणापत्र", d.self_declaration_file],
            ["अर्जदाराचा फोटो", d.photo],
          ]} />
        </Section>

        <Section title="२. शैक्षणिक माहिती">
          {["१२ वी (HSC)", "पदविका / व्यावसायिक", "पदवी", "पदव्युत्तर पदवी"].map((nm, i) => (
            d[`edu_passed_${i}`] === "yes" ? (
              <div key={i} className="mb-2">
                <p className="text-[13px] font-bold text-[#1b5e3f]">{nm}</p>
                <DL rows={[
                  ["बोर्ड / विद्यापीठ", d[`edu_board_${i}`]], ["उत्तीर्ण वर्ष", d[`edu_year_${i}`]],
                  ["टक्केवारी / श्रेणी", d[`edu_pct_${i}`]], ["प्रमाणपत्र", d[`edu_file_${i}`]],
                ]} />
              </div>
            ) : <DL key={i} rows={[[nm, d[`edu_passed_${i}`]]]} />
          ))}
        </Section>

        <Section title="३. संगणक साक्षरता">
          {["MSCIT / समकक्ष", "सायबर सुरक्षाबाबतचे प्रमाणपत्र", "ऑनलाईन अर्जप्रक्रिया व संगणक हाताळणी प्रमाणपत्र"].map((nm, i) => (
            d[`cl_passed_${i}`] === "yes" ? (
              <div key={i} className="mb-2">
                <p className="text-[13px] font-bold text-[#1b5e3f]">{nm}</p>
                <DL rows={[
                  ["अभ्यासक्रमाचे नाव", d[`cl_name_${i}`]], ["उत्तीर्ण वर्ष", d[`cl_year_${i}`]],
                  ["टक्केवारी", d[`cl_pct_${i}`]], ["प्रमाणपत्र", d[`cl_file_${i}`]],
                ]} />
              </div>
            ) : <DL key={i} rows={[[nm, d[`cl_passed_${i}`]]]} />
          ))}
        </Section>

        <Section title="४. पायाभूत सुविधा">
          {INFRA_ITEMS.map((item, i) => (
            <div key={i} className="mb-1">
              <p className="text-[13px] font-semibold text-slate-700">{i + 1}. {item.label}</p>
              <DL rows={[
                ["आहे / नाही", d[`infra_${i}`]],
                ...(d[`infra_${i}`] === "yes" ? [[item.qtyLabel, d[`infra_qty_${i}`]]] : []),
                ...(d[`infra_${i}`] === "yes" && (i === 0 || i === 2) ? [["पुरावा दस्तऐवज", d[`infra_file_${i}`]]] : []),
              ]} />
            </div>
          ))}
        </Section>

        <Section title="५. आर्थिक क्षमता">
          {["Bank statements", "बँकेतील ठेवी (F.D.) / इतर गुंतवणूक", "मागील दोन वर्षांची आयकर विवरणपत्रे", "आर्थिक शाश्वततेचे स्वघोषणापत्र"].map((nm, i) => (
            <div key={i} className="mb-1">
              <p className="text-[13px] font-semibold text-slate-700">{i + 1}. {nm}</p>
              <DL rows={[
                ["आहे / नाही", d[`fin_plan_${i}`]],
                ...(d[`fin_plan_${i}`] === "yes" ? [["तपशील", d[`fin_detail_${i}`]], ["पुरावा दस्तऐवज", d[`fin_file_${i}`]]] : []),
              ]} />
            </div>
          ))}
        </Section>

        <Section title="६. अनुभव">
          <DL rows={[
            ["१. CSC क्षेत्रात अनुभव", d.cscExp],
            ...(d.cscExp === "yes" ? [["CSC ID", d.csc_id], ["पुरावा", d.csc_file]] : []),
            ["२. बँकिंग प्रतिनिधी अनुभव", d.bankRep],
            ...(d.bankRep === "yes" ? [["ID", d.bk_id], ["पुरावा", d.bk_file]] : []),
            ["३. ई-गव्हर्नन्स अनुभव", d.egovExp],
            ...(d.egovExp === "yes" ? [["तपशील", d.eg_details], ["पुरावा", d.eg_file]] : []),
            ["४. महा-ई सेवा केंद्र अनुभव", d.prevCenter],
            ...(d.prevCenter === "yes" ? [["केंद्र नाव व पत्ता", d.pc_addr], ["पुरावा", d.pc_file]] : []),
          ]} />
        </Section>

        <Section title="७. व्यवसाय आराखडा">
          <DL rows={[
            ["सेवा विस्तार", d.bp_text_0], ["जनजागृती", d.bp_text_1],
            ["आर्थिक व्यवहार्यता", d.bp_text_2], ["व्यवसाय नियोजन", d.bp_text_3],
            ["आराखडा दस्तऐवज", d.bp_pdf],
          ]} />
        </Section>

        <Section title="८. सामाजिक समावेशकता">
          <div className="mb-1">
            <p className="text-[13px] font-semibold text-slate-700">१. महिला</p>
            <DL rows={[["होय / नाही", (d.si_female || (d.gender === "स्त्री" ? "yes" : "no")) === "yes" ? "होय" : "नाही"]]} />
          </div>
          {SOCIAL_ITEMS.map((item, i) => (
            <div key={item.key} className="mb-1">
              <p className="text-[13px] font-semibold text-slate-700">{i + 2}. {item.label}</p>
              <DL rows={[
                ["आहे / नाही", d[item.key]],
                ...(d[item.key] === "yes" ? [["पुराव्याची कागदपत्रे", d[`${item.key}_file`]]] : []),
              ]} />
            </div>
          ))}
        </Section>

        <Section title="९. चारित्र्य पडताळणी">
          <DL rows={[
            ["पोलीस ठाण्याचे नाव", d.cv_station], ["क्रमांक", d.cv_number], ["दिनांक", d.cv_date],
            ["पडताळणी पुरावा", d.cv_file], ["आर्थिक गैरव्यवहार समावेश", d.cv_fraud],
            ["काळ्या यादीत समावेश", d.cv_blacklist], ["प्रतिज्ञापत्र", d.cv_affidavit_file],
          ]} />
        </Section>

        <Section title="१०. अर्ज शुल्क">
          <DL rows={[
            ["पेमेंटची पद्धत", d.fee_method], ["UTR क्रमांक", d.fee_utr], ["पेमेंट पुरावा", d.fee_proof],
          ]} />
        </Section>

        <Section title="११. कागदपत्रे (अपलोड केलेली)">
          <DL rows={Object.keys(d)
            .filter((k) => d[k] && typeof d[k] === "object" && d[k].fileId)
            .map((k) => [fileLabel(k), d[k]])} />
        </Section>

        <div className="mt-6 flex items-end justify-between border-t border-slate-200 pt-4">
          <p className="text-[12px] text-slate-500">
            हा संगणकीकृत पावती दस्तऐवज आहे.<br />
            अर्जदाराचे नाव: <strong>{fmt(d.sign_name || d.nameMr)}</strong><br />
            मोबाईल: <strong>{fmt(d.sign_mobile || d.mobile)}</strong><br />
            दिनांक: <strong>{fmt(d.decl_date)}</strong> &nbsp; ठिकाण: <strong>{fmt(d.decl_place)}</strong>
          </p>
          <p className="text-[12px] text-slate-400">Copyright © 2026 Collector Office, Amravati</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <h3 className="mb-2 rounded bg-[#1b5e3f] px-3 py-1.5 text-[14px] font-bold text-white">{title}</h3>
      {children}
    </div>
  );
}
